import { useState, useEffect, useCallback, useMemo } from 'react'
import { Button } from '../ui/button';
import { useNavigate, useLocation } from 'react-router-dom';
import { Copy, Check, ChevronRight, ChevronLeft, ChevronDown, ChevronUp, Settings, Timer, Layers } from 'lucide-react';
// headline images now come from backend deck summaries
import { playerIcons, getPlayerIconById } from '../../lib/playerIcons';
import React from 'react';
import { socketService } from '../../services/socketService';
import { motion, AnimatePresence } from 'framer-motion';
import { trackChosenDeck } from '../../utils/analytics';
import GoogleAdSquare from '../googleads/GoogleAdSquare';

interface Player {
  player_id: string;
  player_name: string;
  role?: string;
  is_spy?: boolean;
  game_location?: string;
  timer_duration_seconds?: number;
  icon_id?: number;
}

interface WaitingRoomProps {
  isAdmin: boolean;
  roomId: string;
  playerName: string;
}

export default function WaitingRoom({ isAdmin, roomId, playerName }: WaitingRoomProps) {
  const location = useLocation();
  const playerId = location.state?.playerId;
  
  // Deck management (data-driven)
  type DeckSummary = { id: string; name: string; description?: string; assetDir: string; cardNoun: string; minCards: number; maxCards: number; headlineImages: string[] };
  const [decks, setDecks] = useState<DeckSummary[]>([]);
  const [currentDeckIndex, setCurrentDeckIndex] = useState(0);
  const [deck, setDeck] = useState<string>('locationDeck');
  // Preselect a representative image per deck so side cards are stable
  const deckImageMap = useMemo(() => {
    const map: Record<string, string> = {};
    decks.forEach((d) => {
      const imgs = d.headlineImages || [];
      map[d.id] = imgs.length ? imgs[Math.floor(Math.random() * imgs.length)] : '';
    });
    return map;
  }, [decks]);

  const randomHeadlineForDeckId = useCallback((deckId: string | undefined) => {
    if (!deckId) return '';
    const d = decks.find(dd => dd.id === deckId);
    const imgs = d?.headlineImages || [];
    if (!imgs.length) return '';
    return imgs[Math.floor(Math.random() * imgs.length)];
  }, [decks]);
  
  // Card count selection state
  const [selectedCardCount, setSelectedCardCount] = useState(30); // Default to 30 cards
  const [maxCardsAvailable, setMaxCardsAvailable] = useState(36); // Default max cards to 36, will be updated
  
  const [players, setPlayers] = useState<Player[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [gameStarting, setGameStarting] = useState(false);
  const [hasNavigated, setHasNavigated] = useState(false);
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();
  const [randomImage, setRandomImage] = useState<string>("");
  // Always assign a random icon on initial join
  const [selectedIconId, setSelectedIconId] = useState<number>(() => (
    Math.floor(Math.random() * playerIcons.length)
  ));
  const [shouldShowRipple, setShouldShowRipple] = useState(false);
  const [rippleCount, setRippleCount] = useState(0);
  // Admin-selectable round timer in minutes (3-15)
  const [selectedTimerMinutes, setSelectedTimerMinutes] = useState<number>(8);
  // Configuration panel collapse state
  const [isConfigExpanded, setIsConfigExpanded] = useState<boolean>(true);
  
  // Create refs for stable references in socket callbacks
  const selectedIconIdRef = React.useRef(selectedIconId);
  const playerNameRef = React.useRef(playerName);
  const isAdminRef = React.useRef(isAdmin);
  const gameStartingRef = React.useRef(gameStarting);
  const hasNavigatedRef = React.useRef(hasNavigated);
  const deckRef = React.useRef(deck);
  // Ensure we only push our initial random icon to the server once
  const hasSyncedInitialIconRef = React.useRef(false);
  const selectedTimerMinutesRef = React.useRef(selectedTimerMinutes);
  const hasAdminEditedTimerRef = React.useRef(false);
  const chosenCardsTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Update refs when state changes
  useEffect(() => {
    selectedIconIdRef.current = selectedIconId;
    playerNameRef.current = playerName;
    isAdminRef.current = isAdmin;
    gameStartingRef.current = gameStarting;
    hasNavigatedRef.current = hasNavigated;
    deckRef.current = deck;
  }, [selectedIconId, playerName, isAdmin, gameStarting, hasNavigated, deck]);

  // Keep timer minutes ref in sync
  useEffect(() => {
    selectedTimerMinutesRef.current = selectedTimerMinutes;
  }, [selectedTimerMinutes]);

  // Handle game started event with player data directly (to avoid race conditions)
  const handleGameStartedWithPlayerData = useCallback(async (currentPlayer: Player) => {
    // Guard: Check and set atomically to prevent duplicate navigation
    if (hasNavigatedRef.current) {
      console.log('Navigation already in progress, ignoring duplicate event');
      return;
    }

    // Set the ref immediately (synchronous) to block subsequent calls
    hasNavigatedRef.current = true;

    try {
      console.log('Game started with player data, fetching stored chosen cards...');
      setIsLoading(true);
      setHasNavigated(true);

      // Fetch stored chosen cards for this room and deck
      const currentDeck = deckRef.current;
      const chosenCardsResponse = await fetch(`/api/rooms/decks/${currentDeck}?roomId=${roomId}`);
      if (!chosenCardsResponse.ok) {
        throw new Error('Failed to fetch chosen cards');
      }
      const chosenCards = await chosenCardsResponse.json();

      if (!Array.isArray(chosenCards) || chosenCards.length === 0) {
        throw new Error('Invalid chosen cards data received');
      }

      // Validate required data
      if (!currentPlayer.role) {
        throw new Error('Player role not assigned');
      }

      if (!currentPlayer.game_location && !currentPlayer.is_spy) {
        throw new Error('Location not assigned for non-spy player');
      }

      const gameData = {
        isAdmin: isAdminRef.current,
        playerName: playerNameRef.current,
        playerId: currentPlayer.player_id,
        role: currentPlayer.role,
        location: currentPlayer.game_location,
        isSpy: currentPlayer.is_spy,
        chosenCards: chosenCards, // Pass the filtered/chosen cards for the game
        timerDuration: currentPlayer.timer_duration_seconds || 480,
        iconId: currentPlayer.icon_id || selectedIconIdRef.current,
        currentDeck: currentDeck, // Pass the current deck (using ref to avoid stale closure)
      };

      console.log('Navigating to game with chosen cards');
      navigate(`/app/play/${roomId}`, { state: gameData });

    } catch (err) {
      console.error('Error handling game start:', err);
      setError(err instanceof Error ? err.message : 'Failed to start game');
      setIsLoading(false);
      setGameStarting(false);
      // Reset the guard on error so user can retry
      hasNavigatedRef.current = false;
      setHasNavigated(false);
    }
  }, [navigate, roomId]);

  // Deck switching functions
  const switchToPreviousDeck = useCallback(() => {
    if (decks.length === 0) return;
    const newIndex = currentDeckIndex === 0 ? decks.length - 1 : currentDeckIndex - 1;
    const newDeck = decks[newIndex]?.id;
    setCurrentDeckIndex(newIndex);
    setDeck(newDeck);
    setRandomImage(randomHeadlineForDeckId(newDeck));
    // Emit deck change via socket
    socketService.changeDeck(newDeck);
  }, [currentDeckIndex, decks]);

  const switchToNextDeck = useCallback(() => {
    if (decks.length === 0) return;
    const newIndex = (currentDeckIndex + 1) % decks.length;
    const newDeck = decks[newIndex]?.id;
    setCurrentDeckIndex(newIndex);
    setDeck(newDeck);
    setRandomImage(randomHeadlineForDeckId(newDeck));
    // Emit deck change via socket
    socketService.changeDeck(newDeck);
  }, [currentDeckIndex, decks]);

  // Function to cycle to the next icon
  const nextIcon = useCallback(() => {
    const newIconId = (selectedIconId + 1) % playerIcons.length;
    setSelectedIconId(newIconId);
    
    // Update icon via socket (converted from REST API)
    if (playerId) {
      socketService.updateIcon(playerId, newIconId);
    }
  }, [selectedIconId, playerId]);

  // Fetch max cards available when deck changes
  useEffect(() => {
    const fetchMaxCards = async () => {
      if (!deck) return;
      try {
        const response = await fetch(`/api/rooms/decks/${deck}?info=true`);
        if (response.ok) {
          const deckInfo = await response.json();
          setMaxCardsAvailable(deckInfo.totalCount);
          setSelectedCardCount(prevCount => {
            if (prevCount > deckInfo.totalCount) {
              return Math.max(25, deckInfo.totalCount);
            }
            return prevCount;
          });
        }
      } catch (error) {
        console.error('Failed to fetch deck info:', error);
        setMaxCardsAvailable(36);
      }
    };
    fetchMaxCards();
  }, [deck]);

  // Debounced function to generate chosen cards
  const debouncedGenerateChosenCards = useCallback(() => {
    if (!isAdmin) return;
    
    // Clear existing timeout
    if (chosenCardsTimeoutRef.current) {
      clearTimeout(chosenCardsTimeoutRef.current);
    }
    
    // Set new timeout
    chosenCardsTimeoutRef.current = setTimeout(async () => {
      try {
        console.log(`Admin generating chosen cards: ${selectedCardCount} from ${deck}`);
        const response = await fetch(`/api/rooms/${roomId}/chosen-cards`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            deckId: deck,
            count: selectedCardCount
          })
        });
        
        if (response.ok) {
          const result = await response.json();
          console.log(`Generated ${result.chosenCards.length} chosen cards for room ${roomId}`);
        } else {
          console.error('Failed to generate chosen cards:', response.status, response.statusText);
        }
      } catch (error) {
        console.error('Error generating chosen cards:', error);
      }
    }, 700);
  }, [isAdmin, deck, selectedCardCount, roomId]);

  // Generate and store chosen cards when admin changes deck or card count
  useEffect(() => {
    debouncedGenerateChosenCards();
    
    // Cleanup timeout on unmount
    return () => {
      if (chosenCardsTimeoutRef.current) {
        clearTimeout(chosenCardsTimeoutRef.current);
      }
    };
  }, [debouncedGenerateChosenCards]);

  // Initialize component
  useEffect(() => {
    setRandomImage(randomHeadlineForDeckId(deck));
    
    // Add current player optimistically for smoother UX
    setPlayers(prevPlayers => {
      if (playerId && !prevPlayers.some(p => p.player_id === playerId)) {
        return [...prevPlayers, {
          player_id: playerId,
          player_name: playerName,
          icon_id: selectedIconId,
        }];
      }
      return prevPlayers;
    });
  }, [selectedIconId, playerName, playerId, deck]);

  // Ripple animation for next deck button every 5 seconds (limited to 5 cycles)
  useEffect(() => {
    if (!isAdmin || rippleCount >= 5) return; // Only for admins and max 5 cycles
    
    const rippleInterval = setInterval(() => {
      setRippleCount(prevCount => {
        const newCount = prevCount + 1;
        if (newCount <= 5) {
          setShouldShowRipple(true);
          // Reset ripple after animation duration (2 seconds for full ripple effect)
          setTimeout(() => setShouldShowRipple(false), 2000);
        }
        return newCount;
      });
    }, 5000);

    // Clear interval when we reach 5 cycles
    if (rippleCount >= 5) {
      clearInterval(rippleInterval);
    }

    return () => clearInterval(rippleInterval);
  }, [isAdmin, rippleCount]);

  // Set up WebSocket connection and event handlers
  useEffect(() => {
    // Connect to WebSocket server
    socketService.connect();

    // Set up Socket event handlers BEFORE joining room to avoid missing events
    socketService.onPlayersUpdated((data: any) => {
      console.log('Players updated:', data);
      if (data?.players) {
        setPlayers(data.players);
        
        // Handle deck synchronization from players-updated event (backup mechanism)
        if (data.currentDeck && data.currentDeck !== deck) {
          console.log('Deck sync via players-updated:', data.currentDeck);
          setDeck(data.currentDeck);
          setRandomImage(randomHeadlineForDeckId(data.currentDeck));
          const idx = decks.findIndex(d => d.id === data.currentDeck);
          if (idx !== -1) setCurrentDeckIndex(idx);
        }
        
        // Determine current player from server payload
        const currentPlayer = data.players.find((p: Player) => p.player_id === playerId);

        // First sync: prefer client's initial random icon (or preselected icon)
        if (currentPlayer && !hasSyncedInitialIconRef.current) {
          if (playerId && selectedIconIdRef.current !== undefined && currentPlayer.icon_id !== selectedIconIdRef.current) {
            socketService.updateIcon(playerId, selectedIconIdRef.current);
          }
          hasSyncedInitialIconRef.current = true;
        } else if (currentPlayer?.icon_id !== undefined && currentPlayer.icon_id !== selectedIconIdRef.current) {
          // Subsequent syncs: follow server updates
          setSelectedIconId(currentPlayer.icon_id);
        }

        // Initialize or sync admin timer from server value
        if (
          isAdminRef.current &&
          !hasAdminEditedTimerRef.current &&
          typeof currentPlayer?.timer_duration_seconds === 'number'
        ) {
          const mins = Math.round((currentPlayer.timer_duration_seconds || 480) / 60);
          const clamped = Math.max(3, Math.min(15, mins));
          if (clamped !== selectedTimerMinutesRef.current) {
            setSelectedTimerMinutes(clamped);
          }
        }
        
        // Check if game has started (roles have been assigned) - for ALL players
        if (currentPlayer?.role && !hasNavigatedRef.current) {
          console.log('Game started detected via players-updated with roles');
          // Call handleGameStarted with the current player data
          handleGameStartedWithPlayerData(currentPlayer);
        }
      }
    });

    socketService.onDeckUpdated((data) => {
      console.log('Deck updated received:', data);
      setDeck(data.deck);
      setRandomImage(randomHeadlineForDeckId(data.deck));
      const idx = decks.findIndex(d => d.id === data.deck);
      if (idx !== -1) setCurrentDeckIndex(idx);
      console.log('Deck state updated to:', data.deck);
    });

    // Join room AFTER setting up event handlers
    socketService.joinRoom(roomId, playerName, isAdmin, playerId);
    

    socketService.onGameStarted(() => {
      console.log('Game started via socket - roles should already be available via players-updated');
      // The actual game start handling is now done in onPlayersUpdated when roles are detected
    });

    socketService.onError((error) => {
      console.error('Socket error:', error);
      setError(error.message);
    });

    // Cleanup on unmount
    return () => {
      socketService.removeAllListeners();
      socketService.disconnect();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- Excluding handleGameStarted to prevent socket reconnection loops
  }, [roomId, playerName, isAdmin, decks, deck]);

  // Reconnect & rejoin when returning to tab (mobile unlock/visibility)
  useEffect(() => {
    const resume = () => socketService.resume();
    const onVisible = () => {
      if (document.visibilityState === 'visible') resume();
    };
    window.addEventListener('focus', resume);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('pageshow', resume);
    return () => {
      window.removeEventListener('focus', resume);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('pageshow', resume);
    };
  }, []);

  // Debounced update of room timer when admin adjusts slider
  useEffect(() => {
    if (!isAdmin) return;
    const controller = new AbortController();
    const timeoutId = setTimeout(async () => {
      try {
        const seconds = Math.floor(selectedTimerMinutes * 60);
        const response = await fetch(`/api/rooms/${roomId}/timer`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ timerDuration: seconds }),
          signal: controller.signal,
        });
        if (!response.ok) {
          console.error('Failed to update timer duration');
        }
      } catch (e) {
        if ((e as any)?.name !== 'AbortError') {
          console.error('Error updating timer duration', e);
        }
      }
    }, 400);
    return () => {
      controller.abort();
      clearTimeout(timeoutId);
    };
  }, [selectedTimerMinutes, isAdmin, roomId]);

  const handleCopyRoomId = async () => {
    try {
      const shareableUrl = `${window.location.origin}/app/join/${roomId}`;
      await navigator.clipboard.writeText(shareableUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      setError('Failed to copy invitation link');
    }
  };

  const handleStartGame = async () => {
    setIsLoading(true);
    setGameStarting(true);
    try {
      console.log('Starting game via socket...');
      
      // Track deck choice analytics
      trackChosenDeck({ 
        deckType: deck
      });
      
      // Ensure latest admin-selected timer is persisted before starting
      if (isAdminRef.current) {
        try {
          const seconds = Math.floor((selectedTimerMinutesRef.current || selectedTimerMinutes) * 60);
          const resp = await fetch(`/api/rooms/${roomId}/timer`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ timerDuration: seconds }),
          });
          if (!resp.ok) {
            console.warn('Timer persist before start failed with status:', resp.status);
          }
        } catch (timerErr) {
          console.warn('Failed to persist timer before starting game', timerErr);
        }
      }

      // Start game via socket (converted from REST API)
      socketService.startGame();
      // Navigation will happen via socket event

      // Persist server-anchored round start time right after starting the game
      try {
        await fetch(`/api/rooms/${roomId}/round-start`, { method: 'POST' });
      } catch (timerErr) {
        console.warn('Failed to set round start time', timerErr);
      }
    } catch (error) {
      console.error('Error starting game:', error);
      setError(error instanceof Error ? error.message : 'Failed to start game');
      setIsLoading(false);
      setGameStarting(false);
    }
  };

  const handleAbortGame = async () => {
    setIsLoading(true);
    try {
      // Delete room via REST API (hybrid approach - REST + Socket broadcast)
      const response = await fetch(`/api/rooms/${roomId}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        throw new Error('Failed to abort game');
      }
      window.location.replace('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to abort game');
    } finally {
      setIsLoading(false);
    }
  };

  // Render player icon
  const renderPlayerIcon = (player: Player) => {
    const isCurrentPlayer = player.player_id === playerId;
    const iconId = isCurrentPlayer ? selectedIconId : (player.icon_id || 0);
    const icon = getPlayerIconById(iconId);
    
    return (
      <div className="flex items-center h-10">
        <div className="flex-shrink-0 w-10 h-10 bg-gray-700 rounded-full flex items-center justify-center">
          <div dangerouslySetInnerHTML={{ __html: icon.svg }} className="w-7 h-7" />
        </div>
        
        {isCurrentPlayer && (
          <button 
            onClick={nextIcon}
            className="flex items-center justify-center h-10 w-6 text-gray-400 hover:text-white p-0"
            aria-label="Next icon"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        )}
      </div>
    );
  };

  // Fetch decks list on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/decks');
        if (res.ok) {
          const list: DeckSummary[] = await res.json();
          setDecks(list);
          // Ensure current deck is a valid id from list
          if (list.length > 0) {
            const initialIdx = Math.max(0, list.findIndex(d => d.id === deck));
            const idx = initialIdx === -1 ? 0 : initialIdx;
            setCurrentDeckIndex(idx);
            setDeck(list[idx].id);
            const imgs = list[idx]?.headlineImages || [];
            setRandomImage(imgs.length ? imgs[Math.floor(Math.random() * imgs.length)] : '');
          }
        }
      } catch (e) {
        // best-effort
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Google AdSense Ad */}
      <GoogleAdSquare />
      <div className="max-w-md mx-auto px-4 py-16">
        {/* Image Card with Deck Selection */}
        <div className="flex items-center justify-center mb-8">
          {isAdmin && (
            <button 
              className="flex items-center justify-center h-12 w-12 text-gray-400 hover:text-white hover:bg-gray-800 rounded-full transition-colors mr-4"
              onClick={switchToPreviousDeck}
              aria-label="Previous deck"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
          )}

          {/* 3D Carousel - show left, center, right cards */}
          <div className="relative w-64 h-64" style={{ perspective: '1000px' }}>
            <AnimatePresence initial={false}>
              {(() => {
                const leftIndex = decks.length ? (currentDeckIndex - 1 + decks.length) % decks.length : 0;
                const rightIndex = decks.length ? (currentDeckIndex + 1) % decks.length : 0;
                const leftDeck = decks[leftIndex]?.id;
                const centerDeck = decks[currentDeckIndex]?.id;
                const rightDeck = decks[rightIndex]?.id;

                const cardBaseClass =
                  'absolute inset-0 w-64 h-64 rounded-lg shadow-lg border-2 border-red-500 overflow-hidden';
                const cardInner = (
                  image: string,
                  forDeck: string
                ) => (
                  <>
                    <img
                      src={image}
                      alt="Deck"
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/20" />
                    <div className="absolute inset-[3px] bg-gray-900/20 rounded-md" />
                    {forDeck === centerDeck && (
                      <div className="absolute bottom-0 left-0 right-0 bg-black/60 backdrop-blur-sm p-3 rounded-b-lg">
                        <p className="text-sm text-gray-300 text-center">
                          Current Deck: <span className="text-red-400 font-semibold capitalize">{decks.find(d => d.id === forDeck)?.name || forDeck}</span>
                        </p>
                      </div>
                    )}
                  </>
                );

                return (
                  <>
                    {/* Left card */}
                    <motion.div
                      key={`left-${leftDeck}`}
                      className={cardBaseClass}
                      initial={{ x: -100, rotateY: 25, scale: 0.9, opacity: 0.6, zIndex: 10 }}
                      animate={{ x: -90, rotateY: 25, scale: 0.9, opacity: 0.6, zIndex: 10 }}
                      exit={{ opacity: 0 }}
                      transition={{ type: 'spring', stiffness: 260, damping: 20 }}
                      onClick={switchToPreviousDeck}
                      style={{ cursor: 'pointer' }}
                    >
                      {cardInner(deckImageMap[leftDeck], leftDeck)}
                    </motion.div>

                    {/* Center card (draggable) */}
                    <motion.div
                      key={`center-${centerDeck}-${randomImage}`}
                      className={cardBaseClass}
                      initial={{ x: 0, rotateY: 0, scale: 1, opacity: 1, zIndex: 30 }}
                      animate={{ x: 0, rotateY: 0, scale: 1, opacity: 1, zIndex: 30 }}
                      exit={{ opacity: 0 }}
                      transition={{ type: 'spring', stiffness: 260, damping: 22 }}
                      drag="x"
                      dragConstraints={{ left: 0, right: 0 }}
                      dragElastic={0.2}
                      onDragEnd={(_, info) => {
                        if (info.offset.x < -60) {
                          switchToNextDeck();
                        } else if (info.offset.x > 60) {
                          switchToPreviousDeck();
                        }
                      }}
                    >
                      {cardInner(randomImage, centerDeck)}
                    </motion.div>

                    {/* Right card */}
                    <motion.div
                      key={`right-${rightDeck}`}
                      className={cardBaseClass}
                      initial={{ x: 100, rotateY: -25, scale: 0.9, opacity: 0.6, zIndex: 10 }}
                      animate={{ x: 90, rotateY: -25, scale: 0.9, opacity: 0.6, zIndex: 10 }}
                      exit={{ opacity: 0 }}
                      transition={{ type: 'spring', stiffness: 260, damping: 20 }}
                      onClick={switchToNextDeck}
                      style={{ cursor: 'pointer' }}
                    >
                      {cardInner(deckImageMap[rightDeck], rightDeck)}
                    </motion.div>
                  </>
                );
              })()}
            </AnimatePresence>
          </div>

          {isAdmin && (
            <div className="relative ml-4">
              <button 
                className="flex items-center justify-center h-12 w-12 text-gray-400 hover:text-white hover:bg-gray-800 rounded-full transition-colors relative z-10"
                onClick={switchToNextDeck}
                aria-label="Next deck"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
              
              {/* Ripple Animation */}
              {shouldShowRipple && (
                <>
                  <div className="absolute inset-0 rounded-full border-2 border-red-400/60 animate-ping" 
                       style={{ animationDuration: '2s' }} />
                  <div className="absolute inset-0 rounded-full border-2 border-red-400/40 animate-ping" 
                       style={{ animationDuration: '2s', animationDelay: '0.3s' }} />
                  <div className="absolute inset-0 rounded-full border-2 border-red-400/20 animate-ping" 
                       style={{ animationDuration: '2s', animationDelay: '0.6s' }} />
                </>
              )}
            </div>
          )}
        </div>

        {/* Deck Description */}
        <div className="text-center mb-4">
          <p className="text-xs text-gray-500 mt-2 max-w-xs mx-auto leading-relaxed">
            {decks.find(d => d.id === deck)?.description || ''}
          </p>
        </div>

        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-2 flex items-center justify-center gap-2">
            Room: <span className="animate-text bg-gradient-to-r from-red-800 via-red-500 to-red-300 bg-clip-text text-transparent">{roomId}</span>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-500/10"
              onClick={handleCopyRoomId}
              title="Copy invitation link"
            >
              {copied ? (
                <Check className="h-4 w-4" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </Button>
          </h1>
          <p className="text-gray-400">
            {copied ? 
              <span className="text-green-500">Invitation link copied to clipboard!</span> :
              "Copy and share the invitation link with your friends!"
            }
          </p>
        </div>

        {/* Game Configuration - Admin Only */}
        {isAdmin && (
          <div className="text-center mb-4">
            <div className="bg-gray-800 rounded-lg p-4">
              <div className={`flex items-center justify-between ${isConfigExpanded ? 'mb-4' : 'mb-0'}`}>
                <div className="flex items-center gap-2">
                  <Settings className="w-5 h-5 text-gray-400" />
                  <h3 className="text-lg font-semibold">Game Configuration</h3>
                </div>
                <button
                  onClick={() => setIsConfigExpanded(!isConfigExpanded)}
                  className="text-gray-400 hover:text-white transition-colors"
                  aria-label={isConfigExpanded ? "Collapse configuration" : "Expand configuration"}
                >
                  {isConfigExpanded ? (
                    <ChevronUp className="w-5 h-5" />
                  ) : (
                    <ChevronDown className="w-5 h-5" />
                  )}
                </button>
              </div>
              <AnimatePresence>
                {isConfigExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    className="overflow-hidden"
                  >
                    <div className="space-y-6">
                <div>
                  <p className="text-sm text-gray-400 mb-3 flex items-center justify-center gap-2">
                    <Timer className="w-4 h-4" />
                    Round Duration: <span className="text-red-400 font-semibold">{selectedTimerMinutes}</span>
                    <span className="text-gray-500"> minutes</span>
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-500 w-6 text-left">3</span>
                      <input
                        type="range"
                        min={3}
                        max={15}
                        step={1}
                        value={selectedTimerMinutes}
                        onChange={(e) => {
                          hasAdminEditedTimerRef.current = true;
                          setSelectedTimerMinutes(Number(e.target.value));
                        }}
                        className="flex-1 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                        style={{
                          background: `linear-gradient(to right, #ef4444 0%, #ef4444 ${((selectedTimerMinutes - 3) / (15 - 3)) * 100}%, #374151 ${((selectedTimerMinutes - 3) / (15 - 3)) * 100}%, #374151 100%)`
                        }}
                      />
                      <span className="text-xs text-gray-500 w-6 text-right">15</span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">Recommended is 1 minute per player.</p>
                </div>

                <div>
                  <p className="text-sm text-gray-400 mb-3 flex items-center justify-center gap-2">
                    <Layers className="w-4 h-4" />
                    Number of Cards: <span className="text-red-400 font-semibold">{selectedCardCount}</span>
                    <span className="text-gray-500"> / {maxCardsAvailable} available</span>
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-500 w-6 text-left">25</span>
                      <input
                        type="range"
                        min="25"
                        max={maxCardsAvailable}
                        value={selectedCardCount}
                        onChange={(e) => setSelectedCardCount(Number(e.target.value))}
                        className="flex-1 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                        style={{
                          background: `linear-gradient(to right, #ef4444 0%, #ef4444 ${((selectedCardCount - 25) / (maxCardsAvailable - 25)) * 100}%, #374151 ${((selectedCardCount - 25) / (maxCardsAvailable - 25)) * 100}%, #374151 100%)`
                        }}
                      />
                      <span className="text-xs text-gray-500 w-10 text-right">{maxCardsAvailable}</span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">Adjust the number of cards for this game</p>
                </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        )}

        <div className="bg-gray-800 rounded-lg p-6 mb-2">
          <h2 className="text-xl font-semibold mb-4">
            Players (<span className={players.length < 3 ? 'text-red-500' : 'text-green-500'}>{players.length}</span>/10)
          </h2>
          <div className="space-y-2">
            {players.map((player) => (
              <div
                key={player.player_id}
                className="flex items-center justify-between p-3 bg-gray-700 rounded-lg"
              >
                <div className="flex items-center">
                  {renderPlayerIcon(player)}
                  <span className="font-medium ml-3">{player.player_name}</span>
                </div>
                {player.player_id === playerId && (
                  <span className="text-sm text-red-400">(You)</span>
                )}
              </div>
            ))}
            {players.length === 0 && (
              <p className="text-gray-400 text-center py-4">No players joined yet</p>
            )}
          </div>
        </div>
        
        {players.length < 3 && (
          <p className="text-gray-400 text-center mb-6">Waiting for players to join...</p>
        )}
        
        {error && (
          <div className="text-red-500 text-sm text-center mb-4">
            {error}
          </div>
        )}

        {isAdmin && (
          <div className="flex gap-4 justify-center">
            <Button
              className="bg-red-500 hover:bg-red-600"
              onClick={handleStartGame}
              disabled={players.length < 3 || isLoading}
            >
              {isLoading ? 'Starting...' : 'Start Game'}
            </Button>
            <Button
              className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
              variant="outline"
              onClick={handleAbortGame}
              disabled={isLoading}
            >
              Abort Game
            </Button>
          </div>
        )}

        {/* Google AdSense Ad (Bottom) */}
        <div className="mt-6">
          <GoogleAdSquare />
        </div>

      </div>
    </div>
  );
} 